import React, { useCallback, useState, type MouseEvent, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import Button from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { FieldHeading } from '@atlassian/jira-issue-field-heading/src/index.tsx';
import {
	FieldWrapper,
	SideBySideField,
	FieldHeadingTitle,
} from '@atlassian/jira-issue-field-heading/src/styled.tsx';

type NestedPanelProps = {
	ariaLabelExpand: string;
	ariaLabelCollapse: string;
	contentId: string;
	children: ReactNode;
	hideTitleOnExpand?: boolean;
	isOpen: boolean;
	label: ReactNode;
	onToggle: (event: MouseEvent) => void;
	showBottomSeparator: boolean;
	title: ReactNode;
	prefetch?: boolean;
};

export const NestedPanel = ({
	ariaLabelExpand,
	ariaLabelCollapse,
	contentId,
	children,
	hideTitleOnExpand = true,
	isOpen,
	label,
	onToggle,
	showBottomSeparator,
	title,
	prefetch = false,
}: NestedPanelProps) => {
	// when using the prefetch mode, rather than load the children immediately, we'll be loading them once the user hovers over the collapsed field
	// note this is currently only available for the jira-work-suggestions-open-in-ide-experiment experiment.
	const [isMouseOver, setIsMouseOver] = useState(false);
	const onMouseOver = useCallback(() => {
		setIsMouseOver(true);
	}, []);

	const handleChevronClick = (e: React.MouseEvent) => {
		e.stopPropagation();

		onToggle(e);
	};

	const handleHeaderClick = (e: React.MouseEvent) => {
		e.stopPropagation();

		// prevent to collapse via header when the header is hidden
		if (hideTitleOnExpand && isOpen) {
			return;
		}
		onToggle(e);
	};

	const controlContentId = `nested-panel-control-id.${contentId}`;

	const NestedPanelField = (
		<FieldWrapper>
			<Box xcss={detailsStyles}>
				<Summary>
					<FieldHeading>
						<NestedPanelFieldHeadingTitle>{label}</NestedPanelFieldHeadingTitle>
					</FieldHeading>
					<SideBySideField>
						<Header
							data-testid="issue-nested-panel.ui.header"
							isOpen={isOpen}
							onClick={handleHeaderClick}
						>
							<FieldTitle data-testid="issue-nested-panel.ui.field-title">
								{(!hideTitleOnExpand || !isOpen) && title}
							</FieldTitle>
							<span>
								<Button
									aria-expanded={isOpen}
									aria-controls={controlContentId}
									aria-label={isOpen ? ariaLabelCollapse : ariaLabelExpand}
									testId="issue-nested-panel.ui.chevron-button"
									iconBefore={
										isOpen ? (
											<ChevronUpIcon
												spacing="spacious"
												testId="issue-nested-panel.ui.chevron-up-icon"
												label=""
												size="small"
											/>
										) : (
											<ChevronDownIcon
												spacing="spacious"
												testId="issue-nested-panel.ui.chevron-down-icon"
												label=""
												size="small"
											/>
										)
									}
									appearance="subtle"
									onClick={handleChevronClick}
									interactionName="auto-generated.issue-nested-panel.ui.chevron-button.button"
								/>
							</span>
						</Header>
					</SideBySideField>
				</Summary>
				<Content
					showBorder={isOpen && showBottomSeparator}
					hidden={!isOpen}
					id={controlContentId}
					data-testid={`issue-nested-panel.ui.content-${controlContentId}`}
				>
					{children}
				</Content>
			</Box>
		</FieldWrapper>
	);

	const NestedPanelFieldNext = (
		<FieldWrapper onMouseOver={onMouseOver} onFocus={onMouseOver} tabIndex={0} role="group">
			<Box xcss={detailsStyles}>
				<Summary>
					<FieldHeading>
						<NestedPanelFieldHeadingTitle>{label}</NestedPanelFieldHeadingTitle>
					</FieldHeading>
					<SideBySideField>
						<HeaderNext
							data-testid="issue-nested-panel.ui.header"
							isOpen={isOpen}
							onClick={handleHeaderClick}
						>
							<FieldTitle data-testid="issue-nested-panel.ui.field-title">
								{(!hideTitleOnExpand || !isOpen) && title}
							</FieldTitle>
							<span>
								<Button
									aria-expanded={isOpen}
									aria-controls={controlContentId}
									aria-label={isOpen ? ariaLabelCollapse : ariaLabelExpand}
									testId="issue-nested-panel.ui.chevron-button"
									iconBefore={
										isOpen ? (
											<ChevronUpIcon
												spacing="spacious"
												testId="issue-nested-panel.ui.chevron-up-icon"
												label=""
												size="small"
											/>
										) : (
											<ChevronDownIcon
												spacing="spacious"
												testId="issue-nested-panel.ui.chevron-down-icon"
												label=""
												size="small"
											/>
										)
									}
									appearance="subtle"
									onClick={handleChevronClick}
									interactionName="auto-generated.issue-nested-panel.ui.chevron-button.button"
								/>
							</span>
						</HeaderNext>
					</SideBySideField>
				</Summary>
				<Content
					showBorder={isOpen && showBottomSeparator}
					hidden={!isOpen}
					id={controlContentId}
					data-testid={`issue-nested-panel.ui.content-${controlContentId}`}
				>
					{prefetch ? (isOpen || isMouseOver) && children : children}
				</Content>
			</Box>
		</FieldWrapper>
	);

	return expVal('jiv-21227_dev_panel_collapsibility_exp', 'isEnabled', false)
		? NestedPanelFieldNext
		: NestedPanelField;
};

const detailsStyles = xcss({
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NestedPanelFieldHeadingTitle = styled(FieldHeadingTitle)({
	marginRight: `${token('space.100')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div<{ isOpen: boolean }>({
	alignItems: 'center',
	display: 'flex',
	gap: token('space.100'),
	paddingTop: 0,
	paddingRight: token('space.100'),
	paddingBottom: 0,
	paddingLeft: token('space.100'),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		background: ({ isOpen }) => (isOpen ? 'inherit' : `${token('elevation.surface.raised')}`),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderNext = styled.div<{ isOpen: boolean }>({
	alignItems: 'center',
	display: 'flex',
	gap: token('space.100'),
	paddingTop: 0,
	paddingBottom: 0,
	paddingLeft: token('space.100'),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		background: ({ isOpen }) => (isOpen ? 'inherit' : `${token('elevation.surface.raised')}`),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Summary = styled.div({
	display: 'flex',
	flexWrap: 'wrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldTitle = styled.span({
	overflow: 'hidden',
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	flexGrow: 1,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 2,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Content = styled.div<{ showBorder: boolean }>({
	paddingBottom: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderBottom: ({ showBorder }) => (showBorder ? `1px solid ${token('color.border')}` : undefined),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const NestedPanelHeadingTitle = styled.span({
	flexGrow: 1,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	minWidth: 0,
});
